'use client';

import { useAuthUser } from '@/hooks/useAuthUser';
import { formatDateHuman } from '@/lib/utils/dateUtils';
import organizationsService from '@/services/organizations-service';
import { daysUntilDeadline } from '@/utils/orgMfaPolicy';
import { ArrowRight, ShieldAlert, X } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';

/**
 * Grace-period notice for an org member who has not yet enrolled MFA (#1887).
 *
 * Shown ONLY in the window the JWT hook leaves open: the org requires MFA
 * (`org_mfa_deadline` is set), the member's own deadline has not passed
 * (`org_require_mfa` is still false — once it flips, both middlewares
 * redirect to /mfa/enroll and this banner is moot), and the member holds no
 * verified factor (`nextLevel !== 'aal2'`). Same fixed-bottom shape as
 * OrgSetupBanner so the two never stack in the header.
 *
 * Dismissible for the SESSION only (sessionStorage): the deadline is real and
 * moves closer each day, so a permanent dismiss would hide the one warning
 * that precedes a lockout; a per-session one keeps it out of the way while
 * the member is mid-task.
 *
 * Copy is deliberately factual — "your organization requires it, by when,
 * where to do it" — and carries no legal framing. The regulatory basis for
 * advocates (SAK, čl. 6 ods. 3 písm. b) is stated on /mfa/enroll for the
 * `professional` trigger, not here: this banner reports the ADMIN's own org
 * policy, which may exist for any reason.
 *
 * ## Co-rendering with OrgSetupBanner
 *
 * Both are `fixed bottom-4`. An OWNER mid-onboarding (org status not yet
 * `active`/`suspended`) who has just turned the requirement on sees both, so
 * for owners this banner reads the same org status OrgSetupBanner keys on and
 * lifts itself one slot up while the setup banner is present. Members never
 * see the setup banner, so they skip that read entirely.
 */

const DISMISSED_SESSION_KEY = 'lexomat.orgMfaGraceBanner.dismissed';

/** Org lifecycle states in which OrgSetupBanner is NOT shown (mirrors its gate). */
const SETUP_BANNER_HIDDEN_STATUSES = new Set(['active', 'suspended']);

export function OrgMfaGraceBanner() {
  const { activeOrg, isLoading, mfa } = useAuthUser();
  const locale = useLocale();
  const t = useTranslations('Organizations');
  const [dismissed, setDismissed] = useState(true);
  // null = not an owner / not loaded; true = OrgSetupBanner occupies bottom-4.
  const [setupBannerShown, setSetupBannerShown] = useState<boolean | null>(
    null
  );

  useEffect(() => {
    try {
      setDismissed(sessionStorage.getItem(DISMISSED_SESSION_KEY) === '1');
    } catch {
      setDismissed(false);
    }
  }, []);

  const orgId = activeOrg?.id;
  const isOwner = activeOrg?.isAdmin ?? false;
  // The banner can only render inside a grace window, so skip the owner's
  // /plan read otherwise — OrgSetupBanner already issues the same call on
  // every page, and get_plan may trigger a lazy Stripe sync.
  const inGraceWindow =
    Boolean(activeOrg?.mfaDeadline) && !activeOrg?.mfaEnforced;
  useEffect(() => {
    if (!orgId || !isOwner || !inGraceWindow) return;
    let cancelled = false;
    organizationsService
      .getOrganizationPlan(orgId)
      .then((res) => {
        if (cancelled) return;
        const status = (res.data as { status?: string } | null)?.status ?? null;
        setSetupBannerShown(
          status === null || !SETUP_BANNER_HIDDEN_STATUSES.has(status)
        );
      })
      .catch(() => {
        if (!cancelled) setSetupBannerShown(false);
      });
    return () => {
      cancelled = true;
    };
  }, [orgId, isOwner, inGraceWindow]);

  if (isLoading || dismissed) return null;
  if (!activeOrg?.mfaDeadline || activeOrg.mfaEnforced) return null;
  // AAL not resolved yet, or a factor is already enrolled — nothing to ask.
  if (mfa.nextLevel === null || mfa.nextLevel === 'aal2') return null;
  // Owner: wait for the status read so the banner never jumps slots.
  if (isOwner && setupBannerShown === null) return null;

  const days = daysUntilDeadline(activeOrg.mfaDeadline) ?? 0;

  const handleDismiss = () => {
    try {
      sessionStorage.setItem(DISMISSED_SESSION_KEY, '1');
    } catch {
      // Storage unavailable — dismiss for this render only.
    }
    setDismissed(true);
  };

  return (
    <div
      className={`fixed ${setupBannerShown ? 'bottom-24' : 'bottom-4'} left-1/2 -translate-x-1/2 z-[99999] max-w-lg w-[calc(100%-2rem)]`}
    >
      <div className='flex items-center gap-2.5 rounded-lg border border-status-warning/40 bg-surface-card shadow-lg px-4 py-2.5'>
        <ShieldAlert
          className='h-4 w-4 text-status-warning flex-shrink-0'
          aria-hidden='true'
        />
        <p className='flex-1 text-sm text-content-primary'>
          {t('mfaGraceBannerMessage', {
            days,
            date: formatDateHuman(activeOrg.mfaDeadline, locale),
          })}
        </p>
        <a
          href={`/${locale}/mfa/enroll`}
          className='shrink-0 flex items-center gap-1 rounded-md bg-brand-primary px-3 py-1 min-h-[44px] text-xs font-medium text-content-inverse hover:bg-brand-primary/90 transition-colors'
        >
          {t('mfaGraceBannerCta')}
          <ArrowRight className='h-3 w-3' aria-hidden='true' />
        </a>
        <button
          type='button'
          onClick={handleDismiss}
          className='shrink-0 flex items-center justify-center min-h-[44px] min-w-[44px] rounded-md text-brand-tertiary/60 hover:text-brand-tertiary transition-colors'
          aria-label={t('mfaGraceBannerDismiss')}
        >
          <X className='h-4 w-4' aria-hidden='true' />
        </button>
      </div>
    </div>
  );
}
